import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Box,
    Typography,
    IconButton,
    Autocomplete,
    MenuItem,
    Divider,
    Avatar,
    ListItem,
    ListItemAvatar,
    ListItemText,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { useDropzone } from 'react-dropzone'
import {
    useSubmitTask,
    useMyAccount,
    useGetMyProjects, // use this to get members
} from '../../../../services/api/task-management/trackerHooks'
import { Link as RouterLink } from 'react-router-dom'
import Link from '@mui/material/Link'

// receive projectData
const SubmitWorkModal = ({ open, onClose, projectData, setSuccessMessage, setErrorMessage }) => {
    const navigate = useNavigate()

    // State for selected task
    const [selectedTask, setSelectedTask] = useState(null)
    const [formData, setFormData] = useState({
        remarks: '',
        links: '',
        attachments: [],
    })

    const [errors, setErrors] = useState({});
    const [reviewer, setReviewer] = useState(null)

    // account (to exclude self)
    const { data: accountData } = useMyAccount()

    // fetch projects (to get members with role=manager)
    const { data: myProjectsData, isLoading: isLoadingProjects } = useGetMyProjects()

    // Submit task mutation
    const { mutate: submitTask, isLoading: isSubmitting } = useSubmitTask()

    // local banner for messages
    const [banner, setBanner] = useState({ open: false, message: '', severity: 'success' })
    const showBanner = useCallback((message, severity = 'success', duration = 5000) => {
        setBanner({ open: true, message, severity })
        if (duration > 0) setTimeout(() => setBanner((b) => ({ ...b, open: false })), duration)
    }, [])

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!open) {
            handleResetForm()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    /**
     * Find the exact project inside myProjects response.
     * Prefer matching by projectData.id (best).
     * Fallback to matching by name/title if id isn't available.
     */
    const currentProject = useMemo(() => {
        const projects = myProjectsData?.data?.projects || []
        if (!Array.isArray(projects) || projects.length === 0) return null

        const projectId = projectData?.id
        if (projectId != null) {
            return projects.find((p) => String(p.id) === String(projectId)) || null
        }

        const projectName = projectData?.name || projectData?.title
        if (projectName) {
            return projects.find((p) => p.name === projectName) || null
        }

        return null
    }, [myProjectsData, projectData])

    const reviewers = useMemo(() => {
        const noReviewer = { id: null, name: 'No Reviewer', staff_id: null, isNoReviewer: true }

        const currentUserId =
            accountData?.data?.staff?.id ||
            accountData?.data?.staff?.staff_id ||
            accountData?.staff?.id

        const members = currentProject?.members || []
        if (!Array.isArray(members) || members.length === 0) return [noReviewer]

        const managers = members
            .filter((m) => String(m?.role || '').toLowerCase() === 'manager')
            .map((m) => {
                const staffId = m?.staff_id || m?.staff?.id
                return {
                    id: staffId ?? null,
                    staff_id: staffId ?? null,
                    name: m?.staff?.name || '',
                    profile_image_url: m?.staff?.profile_image_url,
                    isNoReviewer: false,
                }
            })
            // remove invalid
            .filter((m) => Boolean(m?.staff_id))
            // exclude self
            .filter((m) => !currentUserId || String(m.staff_id) !== String(currentUserId))

        // dedupe by staff_id
        const uniqManagers = Array.from(
            new Map(managers.map((m) => [String(m.staff_id), m])).values()
        )

        return [noReviewer, ...uniqManagers]
    }, [currentProject, accountData])

    /**
     * Tasks:
     * Keep using modal projectData.tasks if it exists (it’s already being used for submission).
     * If tasks are also available in currentProject, fallback to that.
     */
    const getAvailableTasks = () => {
        const tasks =
            projectData?.tasks ||
            currentProject?.tasks ||
            projectData?.data?.project?.tasks ||
            currentProject?.data?.project?.tasks ||
            []

        if (!Array.isArray(tasks)) return []

        return tasks.filter(
            (task) =>
                task.status !== 'completed' &&
                task.status !== 'done' &&
                task.status !== 'submitted_for_review'
        )
    }

    // Handle task selection from dropdown
    const handleTaskChange = (event) => {
        const taskName = event.target.value

        const tasks =
            projectData?.tasks ||
            currentProject?.tasks ||
            projectData?.data?.project?.tasks ||
            currentProject?.data?.project?.tasks ||
            []

        const task = Array.isArray(tasks) ? tasks.find((t) => t.name === taskName) : null
        setSelectedTask(task || null)
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    const handleFileUpload = (acceptedFiles) => {
        if (!acceptedFiles || acceptedFiles.length === 0) return

        const MAX_FILES = 5
        const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MiB

        const currentCount = formData.attachments.length
        const remainingSlots = MAX_FILES - currentCount

        if (remainingSlots <= 0) {
            showBanner('You can only upload up to 5 files maximum.', 'error', 5000)
            return
        }

        const oversizedFiles = acceptedFiles.filter((file) => file.size > MAX_FILE_SIZE)
        const validFiles = acceptedFiles.filter((file) => file.size <= MAX_FILE_SIZE)

        if (oversizedFiles.length > 0) {
            const names = oversizedFiles.map((file) => file.name).join(', ')
            showBanner(
                `The following file(s) cannot be uploaded because they exceed 5MB: ${names}`,
                'error',
                5000
            )
        }

        if (validFiles.length === 0) return

        const filesToAdd = validFiles.slice(0, remainingSlots)

        const newFiles = filesToAdd.map((file, index) => ({
            id: `${file.name}-${file.size}-${Date.now()}-${index}`,
            file,
            name: file.name,
            size: file.size,
            sizeFormatted: formatFileSize(file.size),
            type: file.type,
        }))

        if (validFiles.length > remainingSlots) {
            showBanner(`Only ${remainingSlots} file(s) added. Maximum limit is 5 files.`, 'warning', 5000)
        }

        setFormData((prev) => ({
            ...prev,
            attachments: [...prev.attachments, ...newFiles],
        }))
    }

    // Dropzone configuration
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleFileUpload,
        accept:
            'image/jpeg, image/jpg, image/png, image/gif, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        disabled: formData.attachments.length >= 5,
        multiple: true,
    })

    const handleDeleteFile = (fileId) => {
        setFormData((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((f) => f.id !== fileId),
        }))
    }

    const handleViewFile = (file) => {
        const url = URL.createObjectURL(file.file)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 100)
    }

    // const validate form
    const validateForm = () => {
        const newErrors = {};
        if (!selectedTask) {
            newErrors.task = "Please select a task";
        }

        if (!formData.remarks) {
            newErrors.remarks = "Please enter a remarks";
        }

        if (!reviewer) {
            newErrors.reviewer = "Please select a reviewer or 'No Reviewer'";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Reset form
    const handleResetForm = () => {
        isSUbmittingRef.current = false
        setSelectedTask(null)
        setReviewer(null)
        setFormData({
            remarks: '',
            links: '',
            attachments: []
        })
    }

    // Close handler
    const handleClose = useCallback(() => {
        handleResetForm()
        onClose()
    }, [onClose])

    const isSUbmittingRef = React.useRef(false)

    const handleSubmit = useCallback(() => {

        if (isSUbmittingRef.current) return
        isSUbmittingRef.current = true

        // Validation
        if (!selectedTask) {
            showBanner('Please select a task', 'error', 3000)
            return
        }

        if (!formData.remarks) {
            showBanner('Please enter remarks for your submission', 'error', 3000)
            return
        }

        if (!reviewer) {
            showBanner('Please select a reviewer or choose "No Reviewer" for auto-approval', 'error', 3000)
            return
        }

        // Prepare FormData payload (backend expects multipart/form-data, not JSON)
        const payload = new FormData()
        payload.append('task_id', selectedTask.id)

        if (reviewer && !reviewer.isNoReviewer) {
            payload.append('reviewer_id', reviewer?.staff_id || reviewer?.id || '')
        }

        payload.append('remarks', formData.remarks || '')
        if (formData.links && formData.links.trim() !== '') {
            payload.append('link', formData.links.trim())
        }

        formData.attachments.forEach((fileObj) => {
            payload.append(`attachments[]`, fileObj.file)
        })

        // Call submit task mutation
        submitTask(payload, {
            onSuccess: (data) => {
                isSUbmittingRef.current = false
                const isAutoApproved = data?.data?.task?.auto_approved

                // Build success message with reviewer name and link
                let successMessage = ''
                if (isAutoApproved || !reviewer || reviewer.isNoReviewer) {
                    successMessage = 'Task submitted and auto-approved successfully!'
                } else {
                    const reviewerName = reviewer?.name || 'The reviewer'
                    successMessage = (
                        <span>
                            Task submitted successfully. <strong>{reviewerName}</strong> will be notified to review
                            your work. View your submitted task{' '}
                            <Link
                                component={RouterLink}
                                to="/app/tasks-dashboard/my-tasks?tab=2"
                                underline="always"
                                sx={{ color: '#2666BE', fontWeight: 500, cursor: 'pointer' }}
                            >
                                here
                            </Link>
                        </span>
                    )
                }

                if (typeof setSuccessMessage === 'function') {
                    setSuccessMessage(successMessage)
                } else {
                    showBanner(String(successMessage), 'success', 5000)
                }

                // Close modal and reset form
                handleClose()
            },
            onError: (error) => {
                isSUbmittingRef.current = false
                const validationErrors = error?.response?.data?.errors
                const firstValidationError = Object.values(validationErrors || {})
                    .reduce((messages, fieldErrors) => messages.concat(fieldErrors || []), [])[0]
                const errorMessage =
                    firstValidationError ||
                    error?.response?.data?.message ||
                    'Failed to submit task. Please try again.'
                if (typeof setErrorMessage === 'function') {
                    setErrorMessage(errorMessage)
                } else {
                    showBanner(errorMessage, 'error', 5000)
                }
            },
        })
    }, [formData, selectedTask, reviewer, submitTask, showBanner, setSuccessMessage, setErrorMessage, handleClose])

    // Handle navigation to My Reviews tab
    const handleNavigateToMyReviews = (e) => {
        e.preventDefault()
        navigate('/app/tasks-dashboard/my-tasks?tab=2')
    }

    const availableTasks = getAvailableTasks()

    return (
        <>
            {/* Banner for success/error messages */}
            {banner.open && (
                <Box sx={{
                    position: 'fixed',
                    top: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    width: '100%',
                    maxWidth: 600,
                    px: 2
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor:
                            banner.severity === 'success' ? '#F0FAF4' :
                                banner.severity === 'warning' ? '#FFFBEB' :
                                    '#FFF0F0',
                        borderLeft: '4px solid',
                        borderColor:
                            banner.severity === 'success' ? '#4CAF50' :
                                banner.severity === 'warning' ? '#F59E0B' :
                                    '#EF5350',
                        borderRadius: '4px',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                        py: 1.5,
                        px: 2
                    }}>
                        <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor:
                                banner.severity === 'success' ? '#4CAF50' :
                                    banner.severity === 'warning' ? '#F59E0B' :
                                        '#EF5350',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            {banner.severity === 'success' ? (
                                <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.5 6.79L1.71 4L0.75 4.95L4.5 8.7L11.25 1.95L10.295 1L4.5 6.79Z" fill="white" />
                                </svg>
                            ) : banner.severity === 'warning' ? (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0ZM6.75 9H5.25V7.5H6.75V9ZM6.75 6H5.25V3H6.75V6Z" fill="white" />
                                </svg>
                            ) : (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0ZM6.75 9H5.25V7.5H6.75V9ZM6.75 6H5.25V3H6.75V6Z" fill="white" />
                                </svg>
                            )}
                        </Box>
                        <Typography sx={{
                            fontSize: '14px',
                            color:
                                banner.severity === 'success' ? '#1B5E20' :
                                    banner.severity === 'warning' ? '#78350F' :
                                        '#7A1F1F',
                            fontWeight: 400
                        }}>
                            {banner.message}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2.5 },
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '100%',
                                    backgroundColor: '#E3F2FD',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <UploadFileIcon sx={{ fontSize: 20, color: '#2666BE' }} />
                            </Box>
                            <Typography fontSize={20} fontWeight={600}>
                                Submit Work for Review
                            </Typography>
                        </Box>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ py: 3 }}>

                    {/* Project Info - Auto-filled from projectData */}
                    <Grid container spacing={2} mb={1.5} alignItems="flex-start">
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Project:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body1" fontWeight={600} mt={0.5}>
                                {projectData?.title || projectData?.name || currentProject?.name || 'N/A'}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Reviewer Selection */}
                    <Grid container spacing={2} mb={1.5} alignItems="flex-start">
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500} mb={1} display="block">
                                Reviewer:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                maxWWidth="245px"
                                fullWidth
                                options={reviewers}
                                getOptionLabel={(option) => option?.name || ''}
                                value={reviewer}
                                onChange={(event, newValue) => setReviewer(newValue)}
                                loading={isLoadingProjects}
                                isOptionEqualToValue={(option, value) => {
                                    if (!option || !value) return false
                                    if (option.isNoReviewer && value.isNoReviewer) return true
                                    return String(option.staff_id) === String(value.staff_id)
                                }}
                                renderOption={(props, option) => (
                                    <ListItem {...props} key={option.id || 'no-reviewer'}>
                                        {option.isNoReviewer ? (
                                            <>
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: '#FFA726', width: 32, height: 32 }}>
                                                        <PersonOffIcon sx={{ fontSize: 18 }} />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={option.name}
                                                    secondary="Task will be auto-approved"
                                                    primaryTypographyProps={{ fontWeight: 500 }}
                                                    secondaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={option.profile_image_url}
                                                        sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: '#2F80ED' }}
                                                    >
                                                        {!option.profile_image_url &&
                                                            (option.name || '')
                                                                .split(' ')
                                                                .map((n) => n[0])
                                                                .join('')
                                                                .substring(0, 2)
                                                                .toUpperCase()}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={option.name}
                                                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                                                    secondaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                                />
                                            </>
                                        )}
                                    </ListItem>
                                )}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            outline: 'none !important',
                                            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1) !important',
                                            '&:focus': { outline: 'none !important' },
                                            '&:focus-visible': { outline: 'none !important' },
                                        },
                                    },
                                    popper: {
                                        sx: {
                                            outline: 'none !important',
                                            '& .MuiPaper-root': {
                                                outline: 'none !important',
                                                '&:focus': { outline: 'none !important' },
                                                '&:focus-visible': { outline: 'none !important' },
                                            },
                                            '& .MuiAutocomplete-listbox': {
                                                outline: 'none !important',
                                                '&:focus': { outline: 'none !important' },
                                                '&:focus-visible': { outline: 'none !important' },
                                            },
                                            '& .MuiAutocomplete-option': {
                                                outline: 'none !important',
                                                '&:focus': { outline: 'none !important' },
                                                '&:focus-visible': { outline: 'none !important' },
                                            },
                                        },
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search reviewer"
                                        size="small"
                                        sx={{
                                            outline: 'none !important',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                outline: 'none !important',
                                                '& fieldset': {
                                                    borderColor: '#8A8A8A !important',
                                                    outline: 'none !important',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#8A8A8A !important',
                                                    outline: 'none !important',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#8A8A8A !important',
                                                    borderWidth: '1px !important',
                                                    outline: 'none !important',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: 'none !important',
                                                    outline: 'none !important',
                                                },
                                                '&:focus-visible': {
                                                    outline: 'none !important',
                                                },
                                            },
                                            '& input': {
                                                outline: 'none !important',
                                                '&:focus': {
                                                    outline: 'none !important',
                                                    boxShadow: 'none !important',
                                                },
                                                '&:focus-visible': {
                                                    outline: 'none !important',
                                                },
                                            },
                                            '&:focus-within': {
                                                outline: 'none !important',
                                            },
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <Box component="span" sx={{ ml: 1, mr: 0.5 }}>
                                                        🔍
                                                    </Box>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* Task Selection Dropdown */}
                    <Grid container spacing={2} mb={1.5} alignItems="flex-start">
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500} mb={1} display="block">
                                Task:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                select
                                maxWidth="245px"
                                fullWidth
                                size="small"
                                value={selectedTask?.name || ''}
                                onChange={handleTaskChange}
                                sx={{
                                    outline: 'none !important',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        outline: 'none !important',
                                        '& fieldset': {
                                            borderColor: '#8A8A8A !important',
                                            outline: 'none !important',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#8A8A8A !important',
                                            outline: 'none !important',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#8A8A8A !important',
                                            borderWidth: '1px !important',
                                            outline: 'none !important',
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: 'none !important',
                                            outline: 'none !important',
                                        },
                                        '&:focus-visible': {
                                            outline: 'none !important',
                                        },
                                    },
                                    '& input': {
                                        outline: 'none !important',
                                        '&:focus': {
                                            outline: 'none !important',
                                            boxShadow: 'none !important',
                                        },
                                        '&:focus-visible': {
                                            outline: 'none !important',
                                        },
                                    },
                                    '&:focus-within': {
                                        outline: 'none !important',
                                    },
                                }}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (value) => {
                                        if (!value) {
                                            return (
                                                <Typography variant="body2" color="text.secondary">
                                                    Select a task
                                                </Typography>
                                            )
                                        }
                                        return value
                                    },
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                outline: 'none !important',
                                                boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1) !important',
                                                '&:focus': { outline: 'none !important' },
                                                '&:focus-visible': { outline: 'none !important' },
                                            },
                                        },
                                        sx: {
                                            '& .MuiMenu-list': {
                                                outline: 'none !important',
                                                '&:focus': { outline: 'none !important' },
                                                '&:focus-visible': { outline: 'none !important' },
                                            },
                                            '& .MuiMenuItem-root': {
                                                outline: 'none !important',
                                                '&:focus': { outline: 'none !important' },
                                                '&:focus-visible': { outline: 'none !important' },
                                            },
                                        },
                                    },
                                }}
                            >
                                {availableTasks.length === 0 ? (
                                    <MenuItem disabled>
                                        <Typography variant="body2" color="text.secondary">
                                            No tasks available
                                        </Typography>
                                    </MenuItem>
                                ) : (
                                    availableTasks.map((task, index) => (
                                        <MenuItem key={index} value={task.name}>
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {task.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Value: {task.value}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))
                                )}
                            </TextField>
                        </Grid>
                    </Grid>

                    {/* Value - Auto-filled from selected task */}
                    <Grid container spacing={2} mb={1.5} alignItems="flex-start">
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Value:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body1" fontWeight={600} mt={0.5}>
                                {selectedTask?.value || '-'}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Your Submission Section */}
                    <Typography variant="subtitle2" fontWeight={600} mb={2}>
                        Your Submission:
                    </Typography>

                    {/* Remarks */}
                    <Box mb={3}>
                        <Typography variant="body2" fontWeight={500} mb={1}>
                            Remarks <span style={{ color: '#D32F2F' }}>*</span>
                        </Typography> 
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={10}
                            placeholder="Add remarks"
                            value={formData.remarks}
                            onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                            sx={{
                                outline: 'none !important',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    outline: 'none !important',
                                    '& fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        borderWidth: '1px !important',
                                        outline: 'none !important',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: 'none !important',
                                        outline: 'none !important',
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important',
                                    },
                                },
                                '& textarea': {
                                    outline: 'none !important',
                                    '&:focus': {
                                        outline: 'none !important',
                                        boxShadow: 'none !important',
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important',
                                    },
                                },
                                '&:focus-within': {
                                    outline: 'none !important',
                                },
                            }}
                        />
                    </Box>

                    {/* Links */}
                    <Box mb={3}>
                        <Typography variant="body2" fontWeight={500} mb={1}>
                            Links
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Add a link"
                            value={formData.links}
                            onChange={(e) => setFormData((prev) => ({ ...prev, links: e.target.value }))}
                            inputProps={{ maxLength: 225 }}
                            helperText={`${formData.links.length}/225`}
                            FormHelperTextProps={{
                                sx: { textAlign: 'right' },
                            }}
                            sx={{
                                outline: 'none !important',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    outline: 'none !important',
                                    '& fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        borderWidth: '1px !important',
                                        outline: 'none !important',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: 'none !important',
                                        outline: 'none !important',
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important',
                                    },
                                },
                                '& input': {
                                    outline: 'none !important',
                                    '&:focus': {
                                        outline: 'none !important',
                                        boxShadow: 'none !important',
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important',
                                    },
                                },
                                '&:focus-within': {
                                    outline: 'none !important',
                                },
                            }}
                        />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Attachments Section */}
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Attachments
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formData.attachments.length}/5
                            </Typography>
                        </Box>

                        {/* File Upload Dropzone */}
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragActive ? '#2666BE' : '#E0E0E0',
                                borderRadius: 2,
                                p: 4,
                                textAlign: 'center',
                                backgroundColor: isDragActive ? '#F0F7FF' : '#FAFAFA',
                                cursor: formData.attachments.length >= 5 ? 'not-allowed' : 'pointer',
                                opacity: formData.attachments.length >= 5 ? 0.6 : 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: formData.attachments.length >= 5 ? '#FAFAFA' : '#F5F5F5',
                                    borderColor: formData.attachments.length >= 5 ? '#E0E0E0' : '#2666BE',
                                },
                            }}
                        >
                            <input {...getInputProps()} />
                            <CloudUploadIcon sx={{ fontSize: 48, color: '#9E9E9E', mb: 1 }} />
                            <Typography variant="body1" fontWeight={500} color="text.primary" mb={0.5}>
                                {isDragActive ? 'Drop files here' : 'Drop files here'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                or click to upload (up to 5 files)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                Supported: Images, PDF, Word, Excel (limit to 5MB per file)
                            </Typography>
                        </Box>

                        {/* Uploaded Files List */}
                        {formData.attachments.length > 0 && (
                            <Box mt={2}>
                                {formData.attachments.map((file) => (
                                    <Box
                                        key={file.id}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        p={1.5}
                                        mb={1}
                                        sx={{
                                            border: '1px solid #E0E0E0',
                                            borderRadius: 1.5,
                                            backgroundColor: '#FAFAFA',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" flex={1} minWidth={0}>
                                            <AttachFileIcon
                                                sx={{ fontSize: 20, color: 'text.secondary', mr: 1, flexShrink: 0 }}
                                            />
                                            <Box minWidth={0} flex={1}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {file.sizeFormatted}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box display="flex" gap={1} ml={2} flexShrink={0}>
                                            <Button
                                                size="small"
                                                onClick={() => handleViewFile(file)}
                                                sx={{
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    px: 2,
                                                    fontSize: '0.875rem',
                                                    color: '#2666BE',
                                                }}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={() => handleDeleteFile(file.id)}
                                                sx={{
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    px: 2,
                                                    fontSize: '0.875rem',
                                                    color: '#D32F2F',
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Info Text */}
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            💡 You can upload up to 5 files. Supported formats: Images, PDF, Word, Excel
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, gap: 1 }}>
                    <Button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        sx={{
                            textTransform: 'none',
                            color: 'text.secondary',
                            px: 3,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!selectedTask || isSubmitting}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#F3BF3A',
                            px: '14px',
                            py: '12px',
                            fontWeight: 500,
                            borderRadius: '100px',
                            '&:hover': { backgroundColor: '#D09A01' },
                            '&:disabled': {
                                backgroundColor: '#E0E0E0',
                                color: '#9E9E9E',
                            },
                        }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SubmitWorkModal
